/**
 * SafeVideoPlayer.jsx
 * ─────────────────────────────────────────────────────────────
 * Provider-agnostic kid-safe video player. Given a `video` entry
 * from the topicVideos registry (or from an API response), it
 * picks the right renderer:
 *
 *   provider === "mp4"     → native <video> with src="{video.src}"
 *   provider === "hls"     → native <video> (Safari) or hls.js
 *                            (Chrome/Firefox — dynamic import)
 *   provider === "youtube" → sandboxed youtube-nocookie iframe
 *                            (default when no provider set)
 *
 * Why this exists:
 *   The product team wanted "owned-video swap-in" to be a URL/
 *   provider change on a single entry — no code edits, no page
 *   changes. This module is the swap point.
 *
 * Fallback:
 *   If provider is "mp4"/"hls" and playback errors, we degrade to
 *   the YouTube iframe when `video.id` is present, so a broken
 *   S3/CDN URL never kills the shelf.
 *
 * A11y:
 *   - <video> tags get `controls` + `playsInline`.
 *   - Iframes get sandboxed permissions (no top-level navigation).
 *
 * Props:
 *   - video     { provider?, src?, poster?, id, title }
 *   - autoPlay  boolean (default true — the shelf only mounts
 *               this on user tap, so autoplay is user-initiated).
 *   - onError   optional callback for telemetry / fallback UI.
 *   - testId    prefix for data-testid attributes.
 */
import React, { useEffect, useRef, useState } from "react";

export default function SafeVideoPlayer({
  video, autoPlay = true, onError, testId = "safe-video",
}) {
  const provider = video?.provider || "youtube";
  const [errored, setErrored] = useState(false);

  // If an owned asset failed and there's still a YouTube id
  // available, transparently fall back to it.
  const useFallback = errored && provider !== "youtube" && !!video?.id;
  const effectiveProvider = useFallback ? "youtube" : provider;

  const handleError = (e) => {
    setErrored(true);
    if (typeof onError === "function") onError(e);
  };

  if (effectiveProvider === "mp4") {
    return (
      <video
        data-testid={`${testId}-mp4-${video.id || "owned"}`}
        controls
        autoPlay={autoPlay}
        playsInline
        preload="metadata"
        poster={video.poster || undefined}
        onError={handleError}
        className="absolute inset-0 w-full h-full bg-black"
      >
        <source src={video.src} type="video/mp4" />
        Your browser doesn&rsquo;t support this video.
      </video>
    );
  }

  if (effectiveProvider === "hls") {
    return (
      <HlsVideo video={video} autoPlay={autoPlay} onError={handleError} testId={testId} />
    );
  }

  // Default → YouTube iframe (privacy-enhanced, sandboxed).
  return (
    <iframe
      title={video.title}
      src={`https://www.youtube-nocookie.com/embed/${video.id}?rel=0&modestbranding=1&iv_load_policy=3&playsinline=1${autoPlay ? "&autoplay=1" : ""}`}
      allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      sandbox="allow-scripts allow-same-origin allow-presentation"
      className="absolute inset-0 w-full h-full"
      referrerPolicy="strict-origin-when-cross-origin"
      data-testid={`${testId}-iframe-${video.id}`}
    />
  );
}

/**
 * HlsVideo — dedicated component so we can lazy-load hls.js only when
 * the browser actually needs it (Safari plays HLS natively).
 */
function HlsVideo({ video, autoPlay, onError, testId }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !video?.src) return undefined;

    // Safari / iOS Safari play HLS natively via <source>.
    if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = video.src;
      return undefined;
    }

    // Everyone else → hls.js. Dynamic import so we don't load the
    // ~150 kB library unless an HLS asset is actually requested.
    let hls;
    let cancelled = false;
    import("hls.js").then(({ default: Hls }) => {
      if (cancelled) return;
      if (!Hls.isSupported()) { onError?.(new Error("HLS unsupported")); return; }
      hls = new Hls();
      hls.loadSource(video.src);
      hls.attachMedia(el);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) onError?.(new Error(`HLS fatal ${data.type}`));
      });
    }).catch((e) => onError?.(e));

    return () => { cancelled = true; if (hls) hls.destroy(); };
  }, [video?.src, onError]);

  return (
    <video
      ref={ref}
      data-testid={`${testId}-hls-${video.id || "owned"}`}
      controls
      autoPlay={autoPlay}
      playsInline
      preload="metadata"
      poster={video.poster || undefined}
      onError={onError}
      className="absolute inset-0 w-full h-full bg-black"
    />
  );
}

/** Resolve a video entry's poster/thumbnail URL. Owned first, then
 *  YouTube's high-quality still. */
export function posterFor(video) {
  if (video?.poster) return video.poster;
  if (video?.id)     return `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
  return null;
}
